import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  Alert,
  Snackbar,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  Tooltip,
  Pagination,
  Divider,
  SelectChangeEvent,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Visibility,
  Search,
  Refresh,
  Assignment,
  CheckCircle,
  Pending,
  Warning,
  PriorityHigh,
  Flag,
  Schedule,
  AccessTime,
  DoneAll,
  PlayArrow,
  Check,
  Close,
  Work,
  CalendarToday,
} from '@mui/icons-material';
import { taskAPI, projectAPI } from '../services/api';
import { Task, Project } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { format, differenceInDays, isPast, isToday } from 'date-fns';

const Tasks: React.FC = () => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [openDetails, setOpenDetails] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [viewMode, setViewMode] = useState('list');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [projectFilter, setProjectFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedTasks, setSelectedTasks] = useState<number[]>([]);
  const { user } = useAuth();

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    project: 0,
    assigned_to: 0,
    priority: 'medium',
    status: 'todo',
    due_date: '',
    estimated_hours: 0,
    actual_hours: 0,
  });

  useEffect(() => {
    fetchTasks();
    fetchProjects();
  }, [page, statusFilter, priorityFilter, projectFilter, searchTerm]);

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const response = await taskAPI.getAll();
      let filteredTasks = response.data.results || response.data;
      
      if (statusFilter !== 'all') {
        filteredTasks = filteredTasks.filter((t: Task) => t.status === statusFilter);
      }
      if (priorityFilter !== 'all') {
        filteredTasks = filteredTasks.filter((t: Task) => t.priority === priorityFilter);
      }
      if (projectFilter !== 'all') {
        filteredTasks = filteredTasks.filter((t: Task) => t.project === parseInt(projectFilter));
      }
      if (searchTerm) {
        filteredTasks = filteredTasks.filter((t: Task) => 
          t.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          t.description.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }
      
      setTasks(filteredTasks);
      setTotalPages(Math.ceil(filteredTasks.length / 10));
    } catch (error) {
      console.error('Error fetching tasks:', error);
      showSnackbar('Failed to load tasks', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await projectAPI.getAll();
      setProjects(response.data.results || response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSubmit = async () => {
    try {
      if (editingTask) {
        await taskAPI.update(editingTask.id, formData);
        showSnackbar('Task updated successfully', 'success');
      } else {
        await taskAPI.create(formData);
        showSnackbar('Task created successfully', 'success');
      }
      fetchTasks();
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving task:', error);
      showSnackbar('Failed to save task', 'error');
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this task?')) {
      try {
        await taskAPI.delete(id);
        showSnackbar('Task deleted successfully', 'success');
        fetchTasks();
      } catch (error) {
        console.error('Error deleting task:', error);
        showSnackbar('Failed to delete task', 'error');
      }
    }
  };

  const handleStatusChange = async (id: number, newStatus: string) => {
    try {
      await taskAPI.update(id, { status: newStatus });
      showSnackbar('Task status updated', 'success');
      fetchTasks();
    } catch (error) {
      console.error('Error updating status:', error);
      showSnackbar('Failed to update status', 'error');
    }
  };

  const handleOpenDialog = (task?: Task) => {
    if (task) {
      setEditingTask(task);
      setFormData({
        title: task.title,
        description: task.description,
        project: task.project,
        assigned_to: task.assigned_to,
        priority: task.priority,
        status: task.status,
        due_date: task.due_date,
        estimated_hours: task.estimated_hours,
        actual_hours: task.actual_hours,
      });
    } else {
      setEditingTask(null);
      setFormData({
        title: '',
        description: '',
        project: 0,
        assigned_to: 0,
        priority: 'medium',
        status: 'todo',
        due_date: '',
        estimated_hours: 0,
        actual_hours: 0,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingTask(null);
  };

  const handleProjectFilterChange = (event: SelectChangeEvent) => {
    setProjectFilter(event.target.value);
  };

  const handleStatusFilterChange = (event: SelectChangeEvent) => {
    setStatusFilter(event.target.value);
  };

  const handlePriorityFilterChange = (event: SelectChangeEvent) => {
    setPriorityFilter(event.target.value);
  };

  const handleFormProjectChange = (event: SelectChangeEvent) => {
    setFormData({ ...formData, project: parseInt(event.target.value) });
  };

  const handleFormPriorityChange = (event: SelectChangeEvent) => {
    setFormData({ ...formData, priority: event.target.value });
  };

  const handleFormStatusChange = (event: SelectChangeEvent) => {
    setFormData({ ...formData, status: event.target.value });
  };

  const getPriorityColor = (priority: string): any => {
    const colors: Record<string, any> = {
      low: 'success',
      medium: 'info',
      high: 'warning',
      urgent: 'error',
    };
    return colors[priority];
  };

  const getStatusColor = (status: string): any => {
    const colors: Record<string, any> = {
      todo: 'default',
      in_progress: 'primary',
      review: 'warning',
      done: 'success',
    };
    return colors[status];
  };

  const getDueDateStatus = (dueDate: string) => {
    const date = new Date(dueDate);
    if (isPast(date) && !isToday(date)) return 'overdue';
    if (isToday(date)) return 'today';
    if (differenceInDays(date, new Date()) <= 3) return 'upcoming';
    return 'future';
  };

  const getDueDateColor = (dueDate: string): any => {
    const status = getDueDateStatus(dueDate);
    switch(status) {
      case 'overdue': return 'error';
      case 'today': return 'warning';
      case 'upcoming': return 'info';
      default: return 'success';
    }
  };

  const getProgress = (task: Task) => {
    if (task.estimated_hours === 0) return 0;
    return (task.actual_hours / task.estimated_hours) * 100;
  };

  const getProjectName = (projectId: number) => {
    const project = projects.find(p => p.id === projectId);
    return project ? project.name : 'Unknown Project';
  };

  const stats = {
    total: tasks.length,
    todo: tasks.filter(t => t.status === 'todo').length,
    inProgress: tasks.filter(t => t.status === 'in_progress').length,
    review: tasks.filter(t => t.status === 'review').length,
    done: tasks.filter(t => t.status === 'done').length,
    overdue: tasks.filter(t => getDueDateStatus(t.due_date) === 'overdue' && t.status !== 'done').length,
    highPriority: tasks.filter(t => t.priority === 'high' || t.priority === 'urgent').length,
    completionRate: tasks.length > 0 ? (tasks.filter(t => t.status === 'done').length / tasks.length) * 100 : 0,
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          Task Management
        </Typography>
        <Box>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchTasks} sx={{ mr: 1 }}>
              <Refresh />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
          >
            New Task
          </Button>
        </Box>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography sx={{ color: 'text.secondary' }} gutterBottom>Total Tasks</Typography>
                  <Typography variant="h3">{stats.total}</Typography>
                </Box>
                <Assignment sx={{ fontSize: 40, color: '#1976d2', opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography sx={{ color: 'text.secondary' }} gutterBottom>Completion Rate</Typography>
                  <Typography variant="h3">{stats.completionRate.toFixed(0)}%</Typography>
                </Box>
                <DoneAll sx={{ fontSize: 40, color: '#4caf50', opacity: 0.7 }} />
              </Box>
              <LinearProgress variant="determinate" value={stats.completionRate} sx={{ mt: 1 }} />
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography sx={{ color: 'text.secondary' }} gutterBottom>In Progress</Typography>
                  <Typography variant="h3">{stats.inProgress}</Typography>
                </Box>
                <PlayArrow sx={{ fontSize: 40, color: '#ff9800', opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography sx={{ color: 'text.secondary' }} gutterBottom>Overdue</Typography>
                  <Typography variant="h3" sx={{ color: '#f44336' }}>{stats.overdue}</Typography>
                </Box>
                <Warning sx={{ fontSize: 40, color: '#f44336', opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} sx={{ alignItems: 'center' }}>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search tasks..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={handleStatusFilterChange}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="todo">To Do</MenuItem>
                <MenuItem value="in_progress">In Progress</MenuItem>
                <MenuItem value="review">In Review</MenuItem>
                <MenuItem value="done">Done</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Priority</InputLabel>
              <Select
                value={priorityFilter}
                label="Priority"
                onChange={handlePriorityFilterChange}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="urgent">Urgent</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Project</InputLabel>
              <Select
                value={projectFilter}
                label="Project"
                onChange={handleProjectFilterChange}
              >
                <MenuItem value="all">All Projects</MenuItem>
                {projects.map(project => (
                  <MenuItem key={project.id} value={project.id.toString()}>{project.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {selectedTasks.length > 0 && (
                <Button
                  variant="contained"
                  color="error"
                  size="small"
                  onClick={() => {
                    if (window.confirm(`Delete ${selectedTasks.length} tasks?`)) {
                      selectedTasks.forEach(id => handleDelete(id));
                      setSelectedTasks([]);
                    }
                  }}
                >
                  Delete ({selectedTasks.length})
                </Button>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Task Table */}
      {loading ? (
        <LinearProgress />
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
              <TableRow>
                <TableCell>Task</TableCell>
                <TableCell>Project</TableCell>
                <TableCell>Priority</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Due Date</TableCell>
                <TableCell>Progress</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tasks.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography sx={{ py: 4 }}>
                      No tasks found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                tasks.slice((page - 1) * 10, page * 10).map((task) => (
                  <TableRow key={task.id} hover>
                    <TableCell>
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                          {task.title}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          {task.description.substring(0, 60)}...
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{getProjectName(task.project)}</TableCell>
                    <TableCell>
                      <Chip
                        label={task.priority.toUpperCase()}
                        color={getPriorityColor(task.priority)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={task.status.replace('_', ' ').toUpperCase()}
                        color={getStatusColor(task.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={format(new Date(task.due_date), 'PPP')}>
                        <Chip
                          label={format(new Date(task.due_date), 'MMM dd')}
                          color={getDueDateColor(task.due_date)}
                          size="small"
                          variant="outlined"
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ minWidth: 120 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={getProgress(task)}
                          sx={{ flex: 1, height: 6, borderRadius: 3 }}
                        />
                        <Typography variant="caption">
                          {task.actual_hours}/{task.estimated_hours}h
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          color="info"
                          onClick={() => {
                            setSelectedTask(task);
                            setOpenDetails(true);
                          }}
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleOpenDialog(task)}
                        >
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(task.id)}
                        >
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Pagination */}
      {tasks.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(e, value) => setPage(value)}
            color="primary"
          />
        </Box>
      )}

      {/* Task Form Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingTask ? 'Edit Task' : 'Create New Task'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Task Title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Project</InputLabel>
                <Select
                  value={formData.project.toString()}
                  label="Project"
                  onChange={handleFormProjectChange}
                >
                  {projects.map(project => (
                    <MenuItem key={project.id} value={project.id.toString()}>{project.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Due Date"
                type="date"
                value={formData.due_date}
                onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={formData.priority}
                  label="Priority"
                  onChange={handleFormPriorityChange}
                >
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="urgent">Urgent</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  label="Status"
                  onChange={handleFormStatusChange}
                >
                  <MenuItem value="todo">To Do</MenuItem>
                  <MenuItem value="in_progress">In Progress</MenuItem>
                  <MenuItem value="review">In Review</MenuItem>
                  <MenuItem value="done">Done</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Estimated Hours"
                type="number"
                value={formData.estimated_hours}
                onChange={(e) => setFormData({ ...formData, estimated_hours: parseInt(e.target.value) })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Actual Hours"
                type="number"
                value={formData.actual_hours}
                onChange={(e) => setFormData({ ...formData, actual_hours: parseInt(e.target.value) })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingTask ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Task Details Dialog */}
      <Dialog open={openDetails} onClose={() => setOpenDetails(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h5">Task Details</Typography>
            <IconButton onClick={() => setOpenDetails(false)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedTask && (
            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                {selectedTask.title}
              </Typography>
              
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Work sx={{ mr: 1, color: '#1976d2' }} />
                    <Typography>
                      <strong>Project:</strong> {getProjectName(selectedTask.project)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <PriorityHigh sx={{ mr: 1, color: '#ff9800' }} />
                    <Typography>
                      <strong>Priority:</strong>{' '}
                      <Chip label={selectedTask.priority} color={getPriorityColor(selectedTask.priority)} size="small" />
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Schedule sx={{ mr: 1, color: '#4caf50' }} />
                    <Typography>
                      <strong>Status:</strong>{' '}
                      <Chip label={selectedTask.status} color={getStatusColor(selectedTask.status)} size="small" />
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <CalendarToday sx={{ mr: 1, color: '#f44336' }} />
                    <Typography>
                      <strong>Due Date:</strong> {format(new Date(selectedTask.due_date), 'PPP')}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <AccessTime sx={{ mr: 1, color: '#9c27b0' }} />
                    <Typography>
                      <strong>Hours:</strong> {selectedTask.actual_hours}/{selectedTask.estimated_hours} 
                      ({((selectedTask.actual_hours / selectedTask.estimated_hours) * 100).toFixed(0)}%)
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Description</Typography>
              <Typography variant="body1" sx={{ mb: 3 }}>
                {selectedTask.description}
              </Typography>

              <Typography variant="h6" gutterBottom>Progress</Typography>
              <Box sx={{ mb: 3 }}>
                <LinearProgress
                  variant="determinate"
                  value={getProgress(selectedTask)}
                  sx={{ height: 10, borderRadius: 5 }}
                />
                <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                  {getProgress(selectedTask).toFixed(0)}% Complete
                </Typography>
              </Box>

              <Typography variant="h6" gutterBottom>Quick Actions</Typography>
              <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  startIcon={<PlayArrow />}
                  onClick={() => handleStatusChange(selectedTask.id, 'in_progress')}
                  disabled={selectedTask.status === 'in_progress'}
                >
                  Start Progress
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Check />}
                  onClick={() => handleStatusChange(selectedTask.id, 'review')}
                  disabled={selectedTask.status === 'review'}
                >
                  Mark for Review
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<DoneAll />}
                  onClick={() => handleStatusChange(selectedTask.id, 'done')}
                  disabled={selectedTask.status === 'done'}
                >
                  Complete Task
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDetails(false)}>Close</Button>
          <Button
            variant="contained"
            onClick={() => {
              if (selectedTask) {
                handleOpenDialog(selectedTask);
                setOpenDetails(false);
              }
            }}
          >
            Edit Task
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbars */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Tasks;